import {
  CommonAddress,
  DeepNestedLevelAddress,
  LevelAddress,
  NestedLevelAddress,
  ProtocolConfig,
} from '../types/protocol'
import { BaseDevice } from '../runtime/BaseDevice'
import {
  createHidAckChecker,
  isHidBusyAck,
} from '../chips/shared/protocols/hid-eeprom/HidAckPolicy'
import {
  encodeHidReadPayload,
  encodeHidWritePayload,
} from '../chips/shared/protocols/hid-eeprom/HidCommandCodec'
import { addrToNumber, makeKey } from '../cache'
import type { Tag } from '../cache/types'

/**
 * 缓存 debug 日志开关 (Requirement 5.12)
 *
 * 启用条件(任一为 true 即启用):
 * 1. 环境变量 `RAPOO_SDK_CACHE_DEBUG` 值为 '1' 或 'true'
 * 2. 全局开关 `globalThis.__RAPOO_SDK_CACHE_DEBUG` 为 truthy
 *
 * lazy getter:首次访问读取环境变量并缓存;全局开关每次评估(适配 Electron 渲染进程)。
 */
let _cacheDebugEnv: boolean | undefined
function isCacheDebugEnabled(): boolean {
  if ((globalThis as any).__RAPOO_SDK_CACHE_DEBUG) return true
  if (_cacheDebugEnv === undefined) {
    try {
      const val = process?.env?.RAPOO_SDK_CACHE_DEBUG
      _cacheDebugEnv = val === '1' || val === 'true'
    } catch {
      _cacheDebugEnv = false
    }
  }
  return _cacheDebugEnv
}

/**
 * 辅助：从任意深度的嵌套地址数组中，按索引序列取值
 *
 * @param addressMap - 任意深度的嵌套地址数组（CommonAddress | LevelAddress | NestedLevelAddress | 更深层）
 * @param indices - 索引序列（数量不定，按数组层级从浅到深传入）
 * @returns 计算后的通用地址（CommonAddress = number[]）
 */
export function getAddress(
  addressMap: CommonAddress | LevelAddress | NestedLevelAddress | DeepNestedLevelAddress,
  ...indices: number[]
): CommonAddress {
  // 边界1：如果初始值已经是CommonAddress（number[]），直接返回
  if (Array.isArray(addressMap) && addressMap.every((item) => typeof item === 'number')) {
    return addressMap as CommonAddress
  }

  // 边界2：非数组类型（如单个数字），转为CommonAddress返回
  if (typeof addressMap === 'number') {
    return [addressMap]
  }

  let currentLevel: unknown = addressMap
  for (const index of indices) {
    // 校验当前层级是否为数组，非数组则终止遍历
    if (!Array.isArray(currentLevel)) {
      console.warn(`getAddress: 层级索引 ${index} 超出数组深度，当前层级非数组`, currentLevel)
      break
    }

    // 取当前索引值，越界则取当前层级的第一个元素
    currentLevel = currentLevel[index] ?? currentLevel[0]

    // 提前终止：如果当前层级已是CommonAddress，无需继续遍历
    if (Array.isArray(currentLevel) && currentLevel.every((item) => typeof item === 'number')) {
      break
    }
  }

  // 最终结果处理：确保返回CommonAddress格式
  if (Array.isArray(currentLevel) && currentLevel.every((item) => typeof item === 'number')) {
    return currentLevel as CommonAddress
  }
  if (typeof currentLevel === 'number') {
    return [currentLevel]
  }

  console.error('getAddress: 地址映射表格式不合法，无法解析为CommonAddress', addressMap)
  return []
}

/**
 * 辅助：ACK 校验器
 *
 * @param data - 设备返回的数据
 * @throws {DeviceError} 如果 ACK 字节不为 0x01
 */
export function createCheckACK(config: ProtocolConfig): (data: DataView) => void {
  return createHidAckChecker(config)
}

export function createSendRead(config: ProtocolConfig) {
  const checkACK = createCheckACK(config)
  return async function sendRead<T>(
    device: BaseDevice,
    address: CommonAddress,
    length: number,
    parser: (data: DataView) => T,
    tags?: Tag[],
  ): Promise<T> {
    // Phase 1 并发去重 (Req 1.1~1.6) + Phase 2 会话级缓存 (Req 2.1~2.3, 2.8, 2.9):
    // - 命中分支: 跳过 inflight 与 CommandQueue,基于字节快照重建 DataView 喂给 parser,
    //   业务层无法察觉缓存命中(Req 2.1 / 2.8)。
    // - 未命中分支: 走 inflight 去重 + 真实硬件读;ACK 校验通过后写入字节快照(Req 2.2);
    //   失败路径(超时 / ACK 错误 / parser 抛错)不写入(Req 2.3)。
    // 使用 device.id 作为 key 前缀确保设备级缓存隔离。
    const key = `${device.id}:${makeKey(address, length)}`
    const cached = device.cache.get(key)
    if (cached) {
      if (isCacheDebugEnabled()) {
        console.debug(`[SDK Cache] HIT  key=${key}`)
      }
      const view = new DataView(cached.data.buffer, cached.data.byteOffset, cached.data.byteLength)

      // Shadow Read (Requirement 5.13):
      // 启用时,命中缓存仍发一次真实硬件读,字节级对比,不一致则 console.error + 抛错。
      if (device.cache._shadowReadEnabled) {
        const hwData = await device.sendAndWait(
          config.reportId,
          encodeHidReadPayload(config, device.connectionType, address, length),
          config.featureReportId,
          (res) => !isHidBusyAck(config, res),
        )
        checkACK(hwData)
        const hwBytes = new Uint8Array(hwData.buffer, hwData.byteOffset, hwData.byteLength)
        let mismatch = cached.data.byteLength !== hwBytes.byteLength
        if (!mismatch) {
          for (let i = 0; i < cached.data.byteLength; i++) {
            if (cached.data[i] !== hwBytes[i]) {
              mismatch = true
              break
            }
          }
        }
        if (mismatch) {
          console.error('[SDK Cache] Shadow read mismatch! key=' + key, {
            cached: cached.data,
            hardware: hwBytes,
          })
          throw new Error(
            `[SDK Cache] Shadow read mismatch! key=${key} — cached bytes differ from hardware response`,
          )
        }
      }

      return parser(view)
    }
    if (isCacheDebugEnabled()) {
      console.debug(`[SDK Cache] MISS key=${key}`)
    }
    // 读 miss 走 device.sendAndWait(而非 HidEepromMemoryClient.read),
    // 因为缓存需要原始 DataView 字节快照,client.read 只返回 parser 结果。
    return device.inflight.run(key, async () => {
      const data = await device.sendAndWait(
        config.reportId,
        encodeHidReadPayload(config, device.connectionType, address, length),
        config.featureReportId,
        (res) => !isHidBusyAck(config, res),
      )
      checkACK(data)
      // HardwareCache.set 内部会用 new Uint8Array(...) 再做一次独立内存拷贝(Req 2.9)
      device.cache.set(key, new Uint8Array(data.buffer, data.byteOffset, data.byteLength), {
        tags,
        addressStart: addrToNumber(address),
        length,
      })
      return parser(data)
    })
  }
}

export function createSendWrite(config: ProtocolConfig) {
  const checkACK = createCheckACK(config)
  return async function sendWrite(
    device: BaseDevice,
    address: CommonAddress,
    data: number[],
  ): Promise<void> {
    // Phase 3 写入精化失效 (Req 3.7 / 2.5):
    // - 写入成功(sendAndWait + ACK 校验都通过)后调用
    //   cache.invalidateByAddressRange(addr, data.length) 做地址区间精化失效。
    // - 失败路径(sendAndWait 抛错 / ACK 错误)由 await 顺序自然短路,失效不会执行,
    //   既有缓存保持不变(Req 2.5)。
    const res = await device.sendAndWait(
      config.reportId,
      encodeHidWritePayload(config, device.connectionType, address, data),
      config.featureReportId,
      (r) => !isHidBusyAck(config, r),
    )
    checkACK(res)
    device.cache.invalidateByAddressRange(addrToNumber(address), data.length)
  }
}
